import { ChangeDetectionStrategy, Component, Input, Type } from '@angular/core';
import { EntityWithUuid } from '@nk/ui-lib';
import { Sorting } from '@nk/utils-lib';
import { BehaviorSubject } from 'rxjs';

export interface NkDatatableTableOptions {
  pageOffset: number;
  sortTable: Sorting[];
  searchQuery?: string;
}

export interface NkDatatableColumn<T> {
  prop: keyof T;
  name: string;
  component: Type<NkDatatableRow<T>>;
  sortable: boolean;
}

export interface NkDatatableRow<T> {
  row: T;
  prop: keyof T;
  highlightText?: string;
}

/*
 * Each "Column" component for datatable should extend this abstract one.
 *
 * The reason is when we dynamically create a component and insert it into the view,
 * Angular will invoke each one of the lifecycle hooks except for the ngOnChanges() hook.
 */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export abstract class NkDatatableAbstractColumnComponent<T> {
  @Input() public highlightText?: string;

  @Input() public set row(row: T) {
    this._row = row;
    this._updateValue();
  }
  public get row(): T {
    return this._row;
  }

  @Input() public set prop(prop: keyof T) {
    this._prop = prop;
    this._updateValue();
  }
  public get prop(): keyof T {
    return this._prop;
  }

  private _row!: T;
  private _prop!: keyof T;

  private _value = new BehaviorSubject<{ row: T; prop: keyof T } | null>(null);
  protected value$ = this._value.asObservable();

  private _updateValue(): void {
    if (this._row && this._prop) {
      this._value.next({
        row: this._row,
        prop: this._prop,
      });
    }
  }
}

export interface NkDatatableDetails<T> {
  row: T;
  highlightText?: string;
}

export interface SelectRowEvent<T> {
  action: 'checked' | 'unchecked';
  row: T;
  fullSelection: T[];
}

export interface NkDatatablePagination {
  limit: number;
  pageOffset: number;
  totalCount: number;
}

export interface NkDatatableData<Entity extends EntityWithUuid> {
  entities: Entity[];
  pagination: NkDatatablePagination;
  highlightText?: string;
}

export const DEFAULT_LIMIT = 30;
